import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export type ChatEvent =
  | { type: 'text'; text: string }
  | { type: 'tool_start'; name: string; input: unknown }
  | { type: 'tool_end'; name: string; ok: boolean; summary: string }
  | { type: 'needs_confirmation'; name: string; input: unknown; reason: string }
  | { type: 'error'; message: string }
  | { type: 'done'; stopReason?: string | null };

export type AiStatus = {
  available: boolean;
  reason?: string;
  mcp: boolean;
  tools: { name: string; description: string; mutating: boolean }[];
};

export type ChatMessage = { role: 'user' | 'assistant'; content: string };

export interface AiApi {
  getStatus(): Promise<AiStatus>;
  /** Streams the answer, invoking `onEvent` as each event arrives. */
  chat(
    messages: ChatMessage[],
    options: { allowMutations: boolean; signal?: AbortSignal },
    onEvent: (event: ChatEvent) => void,
  ): Promise<void>;
}

export const aiApiRef = createApiRef<AiApi>({ id: 'plugin.ai.service' });

export class AiClient implements AiApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('ai');
  }

  async getStatus(): Promise<AiStatus> {
    const res = await this.fetchApi.fetch(`${await this.base()}/status`);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return (await res.json()) as AiStatus;
  }

  /**
   * Reads the newline-delimited event stream.
   *
   * Parsed incrementally rather than with res.json(), so text appears as it is
   * generated instead of after the whole answer completes.
   */
  async chat(
    messages: ChatMessage[],
    options: { allowMutations: boolean; signal?: AbortSignal },
    onEvent: (event: ChatEvent) => void,
  ): Promise<void> {
    const res = await this.fetchApi.fetch(`${await this.base()}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, allowMutations: options.allowMutations }),
      signal: options.signal,
    });

    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }

    const reader = res.body?.getReader();
    if (!reader) throw new Error('Streaming is not supported by this browser');

    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // The final chunk may end mid-line, so keep the remainder for next time.
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          onEvent(JSON.parse(line) as ChatEvent);
        } catch {
          // A partial line is not worth surfacing; the next read completes it.
        }
      }
    }
  }
}
